import { DndContext } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import React, { useCallback, useRef, useState } from 'react';
import { TaskCard } from 'shared/components/connected/task-card';
import { OverlayComponentWrapper } from 'shared/components/ui/overlay-component-wrapper';
import { useMouseOutside } from 'shared/hooks/use-mouse-outside';
import { useSortableItemsControls } from 'shared/hooks/use-sortable-items-controls';
import { useSortableSensors } from 'shared/hooks/use-sortable-sensors';
import { ID } from 'shared/types/id';
import { noCollisionDetection } from 'shared/utils/no-collision-detection';

import { SortableOverlay } from '../sortable-overlay';
import { TasksListView } from './tasks-list-view';

export type SelfSortingTaskCardTasksListProps = Omit<
  React.ComponentProps<typeof TasksListView>,
  'onUnfocus' | 'onTask' | 'hasDnd'
> & {
  onReorder?: (ids: ID[]) => void;
  hasOwnSelectedState?: boolean;
};

export const SelfSortingTaskCardTasksList: React.FC<
  SelfSortingTaskCardTasksListProps
> = ({
  tasks,
  selectedTask,
  displayDue,
  showRepeatingAsTodo,
  onReorder,
  hasOwnSelectedState,
  showDetailOnSelect,
  hideGoal,
  hideLifeArea,
  tasksDisabled,
}) => {
  const containerRef = useRef<HTMLUListElement>(null);
  const [activeTask, setActiveTask] = useState<ID>();
  const {
    items,
    activeItem,
    handleDragStart,
    handleDragCancel,
    handleDragEnd,
    setItems,
  } = useSortableItemsControls({ items: tasks, onDragEnd: onReorder });
  const sensors = useSortableSensors();

  const clearSelectedTask = () => setActiveTask(undefined);

  const onMouseLeave = useCallback(() => setItems(tasks), [setItems, tasks]);
  const isOutsideList = useMouseOutside(containerRef, {
    enabled: !!activeItem,
    onMouseLeave,
  });

  return (
    <DndContext
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      sensors={sensors}
      collisionDetection={isOutsideList ? noCollisionDetection : undefined}
    >
      <SortableContext
        items={items.map(({ id }) => id)}
        strategy={verticalListSortingStrategy}
        disabled={!onReorder}
      >
        <TasksListView
          ref={containerRef}
          tasks={items}
          onTask={setActiveTask}
          onUnfocus={clearSelectedTask}
          selectedTask={hasOwnSelectedState ? activeTask : selectedTask}
          displayDue={displayDue}
          showRepeatingAsTodo={showRepeatingAsTodo}
          showDetailOnSelect={showDetailOnSelect}
          hideGoal={hideGoal}
          hideLifeArea={hideLifeArea}
          tasksDisabled={tasksDisabled}
          hasDnd
        />

        <SortableOverlay>
          {!!activeItem && (
            <OverlayComponentWrapper isOutside={isOutsideList}>
              <TaskCard
                task={activeItem.raw.task}
                onUnfocus={clearSelectedTask}
                onClick={setActiveTask}
                isSelected={
                  hasOwnSelectedState
                    ? activeTask === activeItem.id
                    : selectedTask === activeItem.id
                }
                displayDue={displayDue}
                showRepeatingAsTodo={showRepeatingAsTodo}
                showDetailOnSelect={showDetailOnSelect}
                hideGoal={hideGoal}
                hideLifeArea={hideLifeArea}
                isDisabled={tasksDisabled}
              />
            </OverlayComponentWrapper>
          )}
        </SortableOverlay>
      </SortableContext>
    </DndContext>
  );
};
